import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Download, Brain, Search, FileText, Smartphone, ShieldCheck, Zap, Gift } from 'lucide-react';
import { Countdown } from './components/Countdown';
import { TestimonialCarousel } from './components/TestimonialCarousel';
import { FAQAccordion } from './components/FAQAccordion';
import { PurchasePopup } from './components/PurchasePopup';
import { BookBlock } from './components/BookBlock';
import { bookBlocks } from './data/books';
import { useGeoPrice } from './hooks/useGeoPrice';
const COVER_MURDLE_1 = 'https://cdn.magicpatterns.com/uploads/fCGMdVbTpRMd9Urkc6xgfb/murdle_1.png';
const COVER_MURDLE_2 = 'https://cdn.magicpatterns.com/uploads/pLcoYvsRja15vYt1zf2Wr1/MURDLE_2.webp';
const PACK_IMAGE_URL = 'https://cdn.magicpatterns.com/uploads/aGcGXkcU5QVLSeWEm3QF2Y/MOCK_UP.png';
const CHECKOUT_URL = 'https://murdoku.impultienda.ar/checkout/pack-misterios-digitales';
const PACK_ALT = 'Pack Misterios: catorce libros digitales de crímenes, misterios, acertijos y juegos de lógica';
export function App() {
  const {
    price
  } = useGeoPrice();
  useEffect(() => {
    // Meta Pixel Code
    const win = window as any;
    if (!win.fbq) {
      win.fbq = function () {
        win.fbq.callMethod ? win.fbq.callMethod.apply(win.fbq, arguments) : win.fbq.queue.push(arguments);
      };
      if (!win._fbq) win._fbq = win.fbq;
      win.fbq.push = win.fbq;
      win.fbq.loaded = !0;
      win.fbq.version = '2.0';
      win.fbq.queue = [];
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    win.fbq('init', '1052676944287139');
    win.fbq('track', 'PageView');
  }, []);
  const scrollToCheckout = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen font-sans text-[#1E1E1E] bg-[#F7F1E3] overflow-x-hidden" data-id="element-0" data-mp="36huv2f1ajz4">
      <PurchasePopup data-id="element-1" data-mp="38b2ajxh1886" />

      {/* 1. Top Bar */}
      <div className="bg-[#1E1E1E] text-white text-center py-2 text-[11px] md:text-sm font-semibold tracking-widest uppercase px-4" data-id="element-2" data-mp="3ubriinfk2m5k">
        PACK MISTERIOS — 14 LIBROS EN 1 · 100% DIGITAL
      </div>

      {/* 2. Promo Bar */}
      <div className="bg-[#F97316] text-white text-center py-2.5 font-bold flex flex-wrap items-center justify-center gap-x-2 shadow-md sticky top-0 z-50 px-4" data-id="element-3" data-mp="86ckz3qzj5j4">
        <Zap size={18} className="animate-pulse" data-id="element-4" data-mp="3bncpjulogy24" />
        <span className="text-xs md:text-base" data-id="element-5" data-mp="3bkgqg2y3g3c3">OFERTA POR TIEMPO LIMITADO</span>
        <Countdown data-id="element-6" data-mp="3qcim1at4sa6b" />
      </div>

      {/* 3. Hero */}
      <section className="relative bg-[#5E1A8A] text-white pt-10 pb-16 md:pt-14 md:pb-20 overflow-hidden" data-id="element-7" data-mp="3ljxyus4qcvbr">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-8" data-mp="2xu3mscs8jask"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#7B2CBF] rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/3" data-id="element-9" data-mp="2sw1ztgrourbt"></div>

        <div className="max-w-6xl mx-auto px-5 md:px-6 relative z-10" data-id="element-10" data-mp="18czoyofpvnp4">
          <div className="flex flex-col md:grid md:grid-cols-2 gap-6 md:gap-12 items-center" data-id="element-11" data-mp="1d4recvn4zndo">

            {/* Título */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="order-1 md:col-start-1 md:row-start-1 space-y-4 text-center md:text-left" data-id="element-12" data-mp="ygwjb8lnp6xf">
              <div className="flex flex-wrap gap-2 justify-center md:justify-start" data-id="element-13" data-mp="1w210ikjujeym">
                <span className="inline-block bg-[#F97316] text-white font-bold px-3 py-1 rounded-full text-xs md:text-sm" data-id="element-14" data-mp="1i4ch7h5fgfvo">
                  PACK 14 EN 1
                </span>
                
              </div>
              <h1 className="font-heading text-[2rem] leading-[1.1] md:text-6xl font-black text-white drop-shadow-md" data-id="element-15" data-mp="6nmguenoqi9z">
                <span className="text-[#F97316]" data-id="element-16" data-mp="arc3gyksupsd">14 libros</span> para resolver crímenes, misterios y desafíos de lógica
              </h1>
              <p className="text-base md:text-xl text-white/85 font-medium leading-relaxed max-w-md mx-auto md:mx-0" data-id="element-17" data-mp="m6p1qaz14wmj">
                Casos criminales, acertijos, sopas de letras, enigmas visuales, juegos de lógica, escape rooms y mucho más en un solo pack digital.
              </p>
            </motion.div>

            {/* Mockup */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="order-2 md:col-start-2 md:row-start-1 md:row-span-2 relative flex justify-center w-[110%] -ml-[5%] md:w-full md:ml-0" data-id="element-18" data-mp="19uosvc096p1n">
              <div className="absolute inset-0 bg-[#7B2CBF] opacity-40 blur-[50px] rounded-full scale-90" data-id="element-19" data-mp="3kokm7g59f70f"></div>
              <img src={PACK_IMAGE_URL} alt={PACK_ALT} className="relative z-10 w-full max-w-none md:max-w-[640px] h-auto object-contain drop-shadow-[0_20px_40px_rgba(20,5,30,0.8)]" fetchPriority="high" data-id="element-20" data-mp="3gm7pfh8hqjfs" />
            </motion.div>

            {/* Datos + CTA */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="order-3 md:col-start-1 md:row-start-2 space-y-6 w-full text-center md:text-left" data-id="element-21" data-mp="2553hbi6vlbzy">
              <ul className="inline-grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[15px] md:text-base text-left" data-id="element-22" data-mp="2jthr6d2pm0q0">
                {['14 libros digitales', '+1.000 misterios y desafíos', 'Soluciones incluidas', 'Para imprimir o resolver en pantalla', 'Acceso inmediato'].map(item => <li key={item} className="flex items-center gap-2" data-id="element-23" data-mp="thlbty3w8tyu">
                    <CheckCircle2 className="text-[#F97316] shrink-0" size={18} data-id="element-24" data-mp="tjei3calhc6b" />
                    <span data-id="element-25" data-mp="yhg342rkzlkf">{item}</span>
                  </li>)}
              </ul>

              <div data-id="element-26" data-mp="21qsmkk2gsim0">
                <a href="#compra" onClick={scrollToCheckout} className="w-full md:w-auto md:inline-flex bg-[#F97316] hover:bg-[#FB8B3C] text-white font-black text-base md:text-xl py-4 px-8 rounded-xl shadow-[0_8px_0_#9A3412] hover:shadow-[0_4px_0_#9A3412] hover:translate-y-1 transition-all duration-200 flex items-center justify-center gap-3 uppercase" data-id="element-27" data-mp="21s3zb9dw10p9">
                  QUIERO EL PACK MISTERIOS
                  <Download size={20} data-id="element-28" data-mp="2kovep7qb6vo1" />
                </a>
                <div className="flex items-center justify-center md:justify-start gap-2 mt-4 text-[13px] md:text-sm text-white/80 font-medium" data-id="element-29" data-mp="2frv88vpjrqwe">
                  <ShieldCheck size={16} className="text-[#F97316]" data-id="element-30" data-mp="2ti8i53m01fod" />
                  <span data-id="element-31" data-mp="2pgwn2ikcw28s">Acceso inmediato · Producto 100% digital</span>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* 4. Franja de beneficio */}
      <section className="bg-[#2A0B42] text-white py-8 md:py-10 px-5" data-id="element-32" data-mp="2yj5ugzkbvfhy">
        <div className="max-w-3xl mx-auto text-center" data-id="element-33" data-mp="3i33ayynzrqo0">
          <h2 className="font-heading text-lg md:text-2xl font-black text-[#FFB27A] mb-3 uppercase" data-id="element-34" data-mp="3i1jup82c5fm8">
            RECIBÍ LOS 14 LIBROS COMPLETOS EN ESPAÑOL
          </h2>
          <p className="text-sm md:text-lg text-white/85 leading-relaxed" data-id="element-35" data-mp="384nm72cfstho">
            Un pack digital para quienes disfrutan resolver casos, investigar pistas, descubrir culpables y poner a prueba su lógica.
          </p>
        </div>
      </section>

      {/* 5. Qué incluye */}
      <section className="py-14 md:py-20 bg-white relative" data-id="element-36" data-mp="3443sy9uzia0t">
        <div className="max-w-6xl mx-auto px-5 md:px-6" data-id="element-37" data-mp="2pez78iozdnny">
          <div className="text-center mb-10 md:mb-14" data-id="element-38" data-mp="2pginwaeju0q9">
            <h2 className="font-heading text-2xl md:text-5xl font-black text-[#3C1361] mb-2" data-id="element-39" data-mp="2udsie4gsp2ky">
              ¿Qué incluye el Pack Misterios?
            </h2>
            <p className="text-sm md:text-lg text-gray-500 font-semibold" data-id="element-40" data-mp="2gf2m2r1sv6xn">
              14 libros. +1.000 casos, enigmas y juegos. Un solo pack.
            </p>
          </div>

          <div className="space-y-6 md:space-y-8" data-id="element-41" data-mp="30u2imgdgq3gn">
            {bookBlocks.map((book, i) => <BookBlock key={book.id} book={book} reverse={i % 2 === 1} data-id="element-42" data-mp="2vw0yrt87q044" data-mpi={i} />)}
          </div>
        </div>
      </section>

      {/* 6. Bonus Murdle */}
      <section className="py-14 md:py-20 bg-[#1E1E1E] text-white relative overflow-hidden" data-id="element-43" data-mp="2qzaib4shx2i6">
        <div className="absolute top-10 left-6 opacity-5" data-id="element-44" data-mp="2m07d9kcimwa7"><Search size={120} data-id="element-45" data-mp="2hwriktk0b605" /></div>
        <div className="absolute bottom-10 right-6 opacity-5" data-id="element-46" data-mp="2hy2kentw63om"><FileText size={120} data-id="element-47" data-mp="2d59h84e2n3en" /></div>

        <div className="max-w-5xl mx-auto px-5 md:px-6 relative z-10 text-center" data-id="element-48" data-mp="2d484zwlbkavc">
          <span className="inline-flex items-center gap-2 bg-[#F97316] text-white font-black tracking-widest text-[11px] md:text-sm uppercase px-4 py-1.5 rounded-full mb-4" data-id="element-49" data-mp="287rfoz2dvqgo">
            <Gift size={15} data-id="element-50" data-mp="1y91xf56ks3zw" />
            SOLO POR HOY · 2 LIBROS BONUS DE REGALO
          </span>
          <h2 className="font-heading text-2xl md:text-5xl font-black mb-3 leading-tight" data-id="element-51" data-mp="1u6f1as2vfwww">
            2 de tus 14 libros son un <span className="text-[#F97316]" data-id="element-52" data-mp="9po2p2a2f42s">bonus de regalo</span>
          </h2>
          <p className="text-sm md:text-lg text-gray-400 mb-8 max-w-2xl mx-auto" data-id="element-53" data-mp="dpygv280h0sq">
            Murdle Vol. 1 y Vol. 2 ya están dentro del pack: no pagás nada extra por ellos.
          </p>

          <div className="grid sm:grid-cols-2 gap-4 md:gap-6 mb-8 text-left" data-id="element-54" data-mp="nofbc965rxjj">
            {[{
            cover: COVER_MURDLE_1,
            title: 'Murdle Vol. 1',
            desc: 'Acertijos para analizar pistas, relacionar sospechosos y descubrir al culpable.'
          }, {
            cover: COVER_MURDLE_2,
            title: 'Murdle Vol. 2',
            desc: 'Nuevos escenarios, objetos y sospechosos para seguir investigando.'
          }].map(book => <div key={book.title} className="bg-[#2A2A2A] p-5 md:p-6 rounded-3xl border border-[#F97316]/30 flex flex-col sm:flex-row items-center text-center sm:text-left gap-4 relative" data-id="element-55" data-mp="4re2gd9hhdab">
                <span className="absolute -top-2 right-4 bg-[#F97316] text-white text-[10px] font-black px-2.5 py-1 rounded-full shadow-lg" data-id="element-56" data-mp="38dlg1rngdz8r">GRATIS</span>
                <img src={book.cover} alt={book.title} className="w-24 md:w-28 shrink-0 rounded-lg book-shadow" loading="lazy" data-id="element-57" data-mp="2k5ybfvd7a7yh" />
                <div data-id="element-58" data-mp="2p6clfiyd3rz5">
                  <h3 className="font-heading text-lg md:text-xl font-bold text-[#F97316] mb-1.5" data-id="element-59" data-mp="2p5uense2pvcz">{book.title}</h3>
                  <p className="text-sm text-gray-300" data-id="element-60" data-mp="2u2bf9m2gm26y">{book.desc}</p>
                </div>
              </div>)}
          </div>

          <p className="text-sm md:text-base text-white font-bold mb-6" data-id="element-61" data-mp="21fqfpybtyjsb">
            12 libros + 2 bonus = <span className="text-[#F97316]" data-id="element-62" data-mp="26etl5jvq2s0t">14 libros en total</span>
          </p>

          <a href="#compra" onClick={scrollToCheckout} className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#F97316] hover:bg-[#FB8B3C] text-white font-black text-base md:text-xl py-4 px-8 md:px-10 rounded-xl shadow-[0_6px_0_#9A3412] hover:shadow-[0_3px_0_#9A3412] hover:translate-y-1 transition-all duration-200 uppercase tracking-wide" data-id="element-63" data-mp="26cywpvl8shrz">
            QUIERO LOS 14 LIBROS
            <Download size={20} data-id="element-64" data-mp="3vxecjcy6qoky" />
          </a>
        </div>
      </section>

      {/* 7. Todo esto está incluido */}
      <section className="py-14 md:py-20 bg-[#5E1A8A] text-white relative overflow-hidden" data-id="element-65" data-mp="3myu1mxyv9j7a">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-66" data-mp="3hzpv6f3btzkn"></div>
        <div className="max-w-6xl mx-auto px-5 md:px-6 relative z-10" data-id="element-67" data-mp="3hzhj2pf12yai">
          <h2 className="font-heading text-2xl md:text-5xl font-black text-center mb-8 md:mb-14" data-id="element-68" data-mp="3d1fyu15v8sxg">
            Todo esto está incluido
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-id="element-69" data-mp="348ccf3d8t5qo">
            {[{
            title: '14 libros digitales completos',
            icon: FileText
          }, {
            title: '+1.000 casos, enigmas y juegos',
            icon: Brain
          }, {
            title: 'Soluciones explicadas',
            icon: CheckCircle2
          }, {
            title: 'Listos para imprimir',
            icon: Search
          }, {
            title: 'Celular, tablet o computadora',
            icon: Smartphone
          }, {
            title: 'Acceso inmediato',
            icon: Zap
          }].map(item => <div key={item.title} className="bg-white/10 backdrop-blur-sm p-4 md:p-5 rounded-2xl flex items-center gap-3 md:gap-4 border border-white/20" data-id="element-70" data-mp="26n9opejlk6rk">
                <div className="bg-[#F97316] p-2.5 rounded-lg text-white shrink-0" data-id="element-71" data-mp="2aqfvcnamfzyk">
                  <item.icon size={22} data-id="element-72" data-mp="2yepn05uinhxm" />
                </div>
                <span className="font-semibold text-sm md:text-base" data-id="element-73" data-mp="33dadvenvop9e">{item.title}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* 8. Testimonios */}
      <section className="py-14 md:py-20 bg-[#F7F1E3] overflow-hidden" data-id="element-74" data-mp="2tmzabt83wi31">
        <div className="max-w-6xl mx-auto px-5 md:px-6 text-center mb-8 md:mb-10" data-id="element-75" data-mp="2tl4omcp0glxw">
          <h2 className="font-heading text-2xl md:text-5xl font-black text-[#3C1361]" data-id="element-76" data-mp="3hbii87n8usul">
            Lo que dicen quienes ya entraron al mundo de Pack Misterios
          </h2>
        </div>

        <TestimonialCarousel data-id="element-77" data-mp="1amykcsxn33tk" />
      </section>

      {/* 9. Compra */}
      <section id="compra" className="py-16 md:py-24 bg-gradient-to-b from-[#3C1361] to-[#1E1E1E] text-white relative overflow-hidden" data-id="element-78" data-mp="15q84o6pqya8o">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-79" data-mp="1kdkui7ml724v"></div>

        <div className="max-w-6xl mx-auto px-5 md:px-6 relative z-10" data-id="element-80" data-mp="1fgkofwpqlzdr">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 md:gap-y-8 items-center" data-id="element-81" data-mp="rp5gnof6ypv4">

            {/* Título */}
            <div className="md:col-start-2 md:row-start-1 text-center md:text-left" data-id="element-82" data-mp="mrvuqir0gwc1">
              <span className="inline-block bg-[#F97316] text-white font-bold px-3 md:px-4 py-1.5 rounded-full text-xs md:text-sm tracking-wider mb-4" data-id="element-83" data-mp="11owd3vnbbexu">
                PACK 14 EN 1 · 100% DIGITAL
              </span>
              <h2 className="font-heading text-2xl md:text-5xl font-black text-white leading-tight" data-id="element-84" data-mp="2d1p619rua9fh">
                Descargalo hoy y empezá tu primera investigación
              </h2>
            </div>

            {/* Mockup */}
            <div className="md:col-start-1 md:row-start-1 md:row-span-2 relative flex justify-center w-[104%] -ml-[2%] md:w-full md:ml-0" data-id="element-85" data-mp="hp662hhosr1d">
              <div className="absolute inset-0 bg-[#7B2CBF] opacity-30 blur-[40px] rounded-full scale-75" data-id="element-86" data-mp="qn8dpfvxehxm"></div>
              <img src={PACK_IMAGE_URL} alt={PACK_ALT} className="relative z-10 w-full max-w-none md:max-w-[580px] h-auto object-contain drop-shadow-[0_20px_40px_rgba(0,0,0,0.6)]" loading="lazy" data-id="element-87" data-mp="vnnnxykwv9bd" />
            </div>

            {/* Caja de oferta */}
            <div className="md:col-start-2 md:row-start-2 w-full" data-id="element-88" data-mp="15ifonb9sicad">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 p-5 md:p-8 rounded-3xl w-full text-center md:text-left" data-id="element-89" data-mp="1ac0mn3bdv69k">
                <div className="mb-4 flex flex-col md:flex-row items-center md:justify-between gap-2 md:gap-3" data-id="element-90" data-mp="1jc6zqo366487">
                  <span className="text-xs md:text-sm font-bold text-[#F97316] uppercase tracking-wider" data-id="element-91" data-mp="1ob0a47n5tf45">
                    14 libros digitales
                  </span>
                  <Countdown data-id="element-92" data-mp="1ycu8o9ny52cj" />
                </div>

                <p className="text-sm text-white/80 mb-4" data-id="element-93" data-mp="1tf2gdeje3e7t">
                  12 libros + 2 bonus Murdle = 14 libros
                </p>

                <div className="text-4xl md:text-6xl font-black text-white mb-2 font-heading" data-id="element-94" data-mp="1oj3nbdn49812">
                  {price.amount}
                </div>
                <p className="text-[13px] md:text-sm text-white/80 font-medium mb-5" data-id="element-95" data-mp="2h5y6m78761cy">
                  Pago único · Acceso inmediato · Producto digital
                </p>

                <a href={CHECKOUT_URL} className="block w-full bg-[#F97316] hover:bg-[#FB8B3C] text-white font-black text-base md:text-2xl py-4 md:py-5 px-4 md:px-6 rounded-xl shadow-[0_8px_0_#9A3412] hover:shadow-[0_4px_0_#9A3412] hover:translate-y-1 transition-all duration-200 uppercase text-center" data-id="element-96" data-mp="27fvba0dlsbbg">
                  COMPRAR EL PACK MISTERIOS
                </a>

                <div className="flex items-center justify-center md:justify-start gap-2 mt-5 text-[13px] md:text-sm text-white/80" data-id="element-97" data-mp="2v465wlsphera">
                  <ShieldCheck size={18} className="text-[#F97316]" data-id="element-98" data-mp="y6qu0qr72aul" />
                  <span data-id="element-99" data-mp="y936ioe6nt09">Acceso inmediato después del pago</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 10. FAQ */}
      <section className="py-14 md:py-20 bg-[#F7F1E3]" data-id="element-100" data-mp="ofe1ry1hhio1">
        <div className="max-w-4xl mx-auto px-5 md:px-6" data-id="element-101" data-mp="ah68c03nozhm">
          <h2 className="font-heading text-2xl md:text-4xl font-black text-center text-[#3C1361] mb-8 md:mb-10" data-id="element-102" data-mp="ajsfhymmxczb">
            Preguntas Frecuentes
          </h2>
          <FAQAccordion data-id="element-103" data-mp="kjo9dj5mcgn" />
        </div>
      </section>

      {/* 11. Footer */}
      <footer className="bg-[#1E1E1E] text-white/70 py-12 border-t border-white/10" data-id="element-104" data-mp="5hjk6vmahyu1">
        <div className="max-w-6xl mx-auto px-5 md:px-6" data-id="element-105" data-mp="1uwre4cgmc35k">
          <div className="flex flex-col md:flex-row justify-between items-center md:items-start gap-8 mb-10 text-center md:text-left" data-id="element-106" data-mp="1uy2k8ixjdmzu">
            <div className="max-w-md" data-id="element-107" data-mp="3ke50rl2g3rd4">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-3" data-id="element-108" data-mp="3kf6g41qhv00n">
                <div className="bg-[#5E1A8A] p-2 rounded-lg" data-id="element-109" data-mp="3fgmqnrcoz63j">
                  <Brain className="text-[#F97316]" size={24} data-id="element-110" data-mp="3u3y45ysfp06a" />
                </div>
                <h3 className="font-heading font-bold text-xl md:text-2xl text-white tracking-wide" data-id="element-111" data-mp="3pdho06x8b0g8">
                  Atlas Digital Hub
                </h3>
              </div>
              <p className="text-sm text-white/60 mb-3" data-id="element-112" data-mp="71l7arrlw0ev">
                Tienda digital de recursos, libros y productos descargables
              </p>
              <div className="inline-flex items-center gap-2 text-xs font-semibold bg-white/5 px-3 py-1.5 rounded-full text-[#FFB27A]" data-id="element-113" data-mp="gxxynizoi26g">
                <ShieldCheck size={14} data-id="element-114" data-mp="bzebxgd9gmd1" />
                Producto 100% digital · Uso personal
              </div>
            </div>

            <div className="flex flex-col items-center md:items-end gap-3 text-sm" data-id="element-115" data-mp="2dpy5er5brgkm">
              <h4 className="font-heading font-bold text-white tracking-widest mb-1" data-id="element-116" data-mp="2ds2fjqtthbtc">
                PACK MISTERIOS
              </h4>
              <a href="#" className="hover:text-white transition-colors" data-id="element-117" data-mp="1wvdgk2r31k3a">
                Términos y Condiciones
              </a>
              <a href="#" className="hover:text-white transition-colors" data-id="element-118" data-mp="21nwuj0tomh8m">
                Políticas de Privacidad
              </a>
              <a href="#" className="hover:text-white transition-colors" data-id="element-119" data-mp="26nj8xdl8y3j0">
                Contacto
              </a>
            </div>
          </div>

          <div className="pt-6 border-t border-white/10 text-center text-xs text-white/40 flex flex-col md:flex-row justify-between items-center gap-3" data-id="element-120" data-mp="1dy2jmosxb2r0">
            <p data-id="element-121" data-mp="1iygt8ba6akr5">
              © {new Date().getFullYear()} Atlas Digital Hub. Todos los derechos reservados.
            </p>
            <p data-id="element-122" data-mp="2ubsovyvs3x28">Las marcas y títulos pertenecientes a terceros se ofrecen únicamente cuando existe autorización legal para su distribución.</p>
          </div>
        </div>
      </footer>
    </div>;
}